import {Box, Pagination, Paper, InputAdornment, TextField, useTheme, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import CustomTypography from "../../../core/components/ui/CustomTypography";
import CustomButton from "../../../core/components/ui/CustomButton";
import { useGates } from "../hooks/useGates";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomTableLoaderComponent from "../../../core/components/common/custom-table-loader.component";
import ListGateComponent from "../components/list-gate.component";
import { RBACUIGuard } from "../../RBACPlugin";
import { permissions } from "../../../core/components/common/variables/static_variables";
import useGateStore from "../zustand/useGateStore";
import CustomFilterComponent from "../../../core/components/common/custom-filter.component";

const headers = ["Gate Name", "Type", "Location", "Status", "Actions"];
const rowsPerPage = 10;

const ListGatePage = (props) => {

    const {isMobile, breakpoints} = props;

    const theme = useTheme();

    const [gateList, setGateList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    
    const {isLoadingGetGates, gates, errorGetGates, getGates, filterGate, isFilteringGate} = useGates()
    const {isGateCreated, isGateStatusUpdated, isGateDeleted} = useGateStore()
    const navigate = useNavigate();

    const [filterLabel, setFilterLabel] = useState(null);
    const [filter, setFilter] = useState(null);
    const [gateNameFilterValue, setGateNameFilterValue] = useState(null);
    const [locationFilterValue, setLocationFilterValue] = useState(null);
    const [typeFilterValue, setTypeFilterValue] = useState(null);
    const [statusFilterValue, setStatusFilterValue] = useState(null);

    const searchForOptions = [
        {value: "gatename", label: "Gate Name"},
        {value: "type", label: "Type"},
        {value: "location", label: "Location"},
        {value: "status", label: "Status"},
    ]

    const gateTypeData = [
        {value: "In", label: "In"},
        {value: "Out", label: "Out"},
    ]

    const gateStatusData = [
        {value: "Active", label: "Active"},
        {value: "Deactivated", label: "Deactivated"},
    ]

    useEffect(() => {
        if (gates) {
            setGateList(gates?.content);
            setTotalPages(gates?.totalPages);
        }
    }, [gates])

    useEffect(() => {
        getGates(currentPage, rowsPerPage);
    }, [currentPage])

    useEffect(() => {
        if (isGateCreated || isGateStatusUpdated || isGateDeleted) {
            getGates(currentPage, rowsPerPage)
        }
    }, [currentPage, isGateCreated, isGateStatusUpdated, isGateDeleted])

    useEffect(() => {
        if (filter && typeFilterValue) {
            const parameters = {
                page: 1,
                limit: 20,
                searchFor: filter,
                searchVal: typeFilterValue,
            }
                    
            filterGate(parameters);
                
        } else if (filter && statusFilterValue) {
            const parameters = {
                page: 1,
                limit: 20,
                searchFor: filter,
                searchVal: statusFilterValue,
            }
                    
            filterGate(parameters);   
        }
    }, [typeFilterValue, statusFilterValue])


    const handleChangePagination = (event, page) => { 
        setCurrentPage(page); 
    }

    const handleClickOpen = () => {
        navigate("/gates/create");
    }

    const handleSearchForOption = (selectedOption, type) => { 
        if (selectedOption && type === "type") {
            setFilter(selectedOption.value);
            setFilterLabel(selectedOption.label);
            setStatusFilterValue(null);

        } else if (selectedOption && type === "status") {
            setFilter(selectedOption.value);
            setFilterLabel(selectedOption.label);
            setTypeFilterValue(null);

        } else {    
            handleSearchForClearFilter();
        }
    }

    const handleSearchForClearFilter = (e) => {
        setFilter(null);
        setFilterLabel(null);

        getGates(currentPage, rowsPerPage);
    }

    const handleSearchValOption = (selectedOption, type) => {
        if (selectedOption && type === "type") {
            setTypeFilterValue(selectedOption.label);
            setStatusFilterValue(null);

        } else if (selectedOption && type === "status") {
            setStatusFilterValue(selectedOption.label); 
            setTypeFilterValue(null);

        } else {    
            handleSearchValClearFilter();
        }
    }

    const handleSearchValClearFilter = (e) => {
        setTypeFilterValue(null);
        setStatusFilterValue(null);
        setGateNameFilterValue("");
        setLocationFilterValue("");

        getGates(currentPage, rowsPerPage);
    }

    const handleTextfield = (e, type) => {
        setTypeFilterValue(null);
        setStatusFilterValue(null);

        if (type === "gateName") {
            setGateNameFilterValue(e.target.value);
            setLocationFilterValue("");

        } else if (type === "location") {
            setLocationFilterValue(e.target.value);
            setGateNameFilterValue("");
        }
    }

    const handleKeyPress = (e, type) => {
        if (e.keyCode === 13 || e.key === "Enter") {
            if (filter && type === "gateName" && gateNameFilterValue) {
                const parameters = {
                    page: 1,
                    limit: 20,
                    searchFor: filter,
                    searchVal: gateNameFilterValue,
                }
                
                filterGate(parameters);

            }  else if (filter && type === "location" && locationFilterValue) {
                const parameters = {
                    page: 1,
                    limit: 20,
                    searchFor: filter,
                    searchVal: locationFilterValue,                      
                }

                filterGate(parameters);
            }
        }
    }



    return (
        <Box>
            <Box sx={{textAlign: "start"}}>
                <CustomTypography 
                    variant="h3"
                    text={"Gates"}
                    customStyles={{
                        marginBottom: 0, 
                    }}
                />

                <CustomTypography 
                    variant="subtitle1"
                    text={"Manage and monitor all gates operations seamlessly and efficiently"}
                    customStyles={{
                        marginBottom: 0, 
                    }}
                />
            </Box>

            <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px", mt: 3}}>
                <Box sx={{display: "flex", justifyContent: "right"}}>
                    <RBACUIGuard requiredPermission={permissions.containers.FILTER_LIST_GATE}>
                        <CustomFilterComponent
                            width={250}
                            isSearchable={true}
                            isClearable={true}
                            filterLabel={
                                    (filterLabel === "Type") ? "Type" 
                                :  
                                    (filterLabel === "Status") ? "Status" 
                                : 
                                    (filterLabel === "Gate Name") ? "Gate Name" 
                                : 
                                    (filterLabel === "Location") ? "Location" 
                                : 
                                    null
                            }
                            filterOptions={searchForOptions}
                            onChange={selectedOption => handleSearchForOption(selectedOption, filterLabel === "Type" ? "type" : "status")}
                            isSearchForFilter={true}
                            isSearchValue={false}
                        />
                    </RBACUIGuard>

                    <RBACUIGuard requiredPermission={permissions.containers.GET_CONTAINER_TYPE}>
                        {(filterLabel === null || filterLabel === "Type") && 
                            <CustomFilterComponent
                                width={300}
                                isSearchable={true}
                                isClearable={true}
                                filterLabel={filterLabel}
                                filterOptions={gateTypeData}
                                onChange={selectedOption => handleSearchValOption(selectedOption, "type")}
                                isSearchForFilter={false}
                                isSearchValue={true}
                                isDisabled={false}
                            />
                        }
                    </RBACUIGuard>

                    {(filterLabel === "Status") && 
                        <CustomFilterComponent
                            width={300}
                            isSearchable={true}
                            isClearable={true}
                            filterLabel={filterLabel}
                            filterOptions={gateStatusData}
                            onChange={selectedOption => handleSearchValOption(selectedOption, "status")}
                            isSearchForFilter={false}
                            isSearchValue={true}
                            isDisabled={false}
                        />
                    }

                    {(filterLabel === "Gate Name") &&
                        <TextField
                            value={gateNameFilterValue}
                            placeholder={"Input Gate Name..."}
                            variant="outlined"
                            autoComplete="off"  
                            onChange={(e) => handleTextfield(e, "gateName")}
                            onKeyDown={(e) => handleKeyPress(e, "gateName")}
                            slotProps={{
                                input: {
                                    style: {
                                        padding: "10px",
                                        fontSize: 14,
                                        fontStyle: 'italic'
                                    },
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography sx={{fontSize: 14, color: theme.typography.primary, opacity: 0.85, fontStyle: "normal"}}>
                                                Gate Name:
                                            </Typography>
                                        </InputAdornment>
                                    )
                                }
                            }}
                            sx={{
                                marginRight: "10px", 
                                "& .MuiInputBase-root": {
                                    height: 42, 
                                    backgroundColor: "transparent", 
                                    width: 340, 
                                    borderColor: "#dce8f5"
                                },
                                "& .MuiOutlinedInput-root": {
                                    height: 42,
                                    backgroundColor: "transparent",
                                    "& fieldset": {
                                        borderColor: "#dce8f5",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: theme.palette.primary.main, 
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: `0 0 0 1px ${theme.palette.primary.main}`,
                                    }
                                },
                            }}  
                        /> 
                    }

                    {(filterLabel === "Location") &&
                        <TextField
                            value={locationFilterValue}
                            placeholder={"Input Location..."}
                            variant="outlined"
                            autoComplete="off"  
                            onChange={(e) => handleTextfield(e, "location")}
                            onKeyDown={(e) => handleKeyPress(e, "location")}
                            slotProps={{
                                input: {
                                    style: {
                                        padding: "10px",
                                        fontSize: 14,
                                        fontStyle: 'italic'
                                    },
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography sx={{fontSize: 14, color: theme.typography.primary, opacity: 0.85, fontStyle: "normal"}}>
                                                Location:
                                            </Typography>
                                        </InputAdornment>
                                    )
                                }
                            }}
                            sx={{
                                marginRight: "10px", 
                                "& .MuiInputBase-root": {
                                    height: 42, 
                                    backgroundColor: "transparent", 
                                    width: 340, 
                                    borderColor: "#dce8f5"
                                },
                                "& .MuiOutlinedInput-root": {
                                    height: 42,
                                    backgroundColor: "transparent",
                                    "& fieldset": {
                                        borderColor: "#dce8f5",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: theme.palette.primary.main, 
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: `0 0 0 1px ${theme.palette.primary.main}`,
                                    }
                                },
                            }}  
                        /> 
                    }

                    <RBACUIGuard requiredPermission={permissions.gate.CREATE_GATE}>
                        <CustomButton 
                            variant="contained" 
                            startIcon={<AddIcon fontSize="small" />}
                            label="Add Gate"
                            customStyles={{fontSize: 14}}
                            onClick={handleClickOpen}
                        />
                    </RBACUIGuard>
                </Box>

                {(isLoadingGetGates || isFilteringGate) ?
                    <Box sx={{mt: 3}}>
                        <CustomTableLoaderComponent 
                            headers={headers} 
                            hasAction={false}
                        /> 
                    </Box>
                :   
                    <Box sx={{mt: 3}}>
                        <ListGateComponent 
                            headers={headers} 
                            data={gateList} 
                            isMobile={isMobile}
                            breakpoints={breakpoints}
                        />
                
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handleChangePagination}
                            sx={{
                                margin: "auto",
                                width: "fit-content",
                                paddingTop: 3,
                
                                "& .MuiPaginationItem-root.Mui-selected": {
                                    backgroundColor: "#0b99da",      
                                    color: "#fff",               
                                }
                            }}
                        />
                    </Box>
                }
            </Paper>
        </Box>
    )
}

export default ListGatePage